import React, { forwardRef, useCallback } from 'react'

import { type InfiniteData } from '@tanstack/react-query'
import { HiBookmark, HiOutlineBookmark } from 'react-icons/hi2'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import IconButton, {
  type IconButtonProps,
} from '@/components/common/IconButtonV2'
import { ResourcePermissionGate } from '@/components/common/PermissionGate'

import { IS_FEDRAMP_ENVIRONMENT } from '@/constants/env'

import { useBookmarks } from '@/hooks/bookmarks/useBookmarks'
import { useCheckBookmark } from '@/hooks/bookmarks/useCheckBookmark'
import { useCreateBookmark } from '@/hooks/bookmarks/useCreateBookmark'
import { useDeleteBookmark } from '@/hooks/bookmarks/useDeleteBookmark'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationUser } from '@/hooks/organization/useOrganizationUsers'
import { usePursuitIsBookmarked } from '@/hooks/pursuitBookmarks/useBookmarkedPursuitIds'
import { useCreatePursuitBookmark } from '@/hooks/pursuitBookmarks/useCreatePursuitBookmark'
import { useDeletePursuitBookmark } from '@/hooks/pursuitBookmarks/useDeletePursuitBookmark'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { Bookmark, BookmarkableType } from '@/types/bookmark/Bookmark'
import { type UUID, isUUID } from '@/types/Id'

// region Base

export interface BookmarkIconButtonBaseProps extends Omit<
  IconButtonProps,
  'onClick' | 'children'
> {
  isBookmarked: boolean
  isPending: boolean
  isLoading: boolean
  onClick: () => void
  iconSize?: number
  iconClassName?: string
  activeIconClassName?: string
  inactiveIconClassName?: string
  tooltip?: string
}

export const BookmarkIconButtonBase = forwardRef<
  HTMLButtonElement,
  BookmarkIconButtonBaseProps
>(
  (
    {
      isBookmarked,
      isPending,
      isLoading,
      onClick,
      iconSize = 18,
      iconClassName,
      activeIconClassName,
      inactiveIconClassName,
      tooltip,
      disabled,
      className,
      ...rest
    },
    ref,
  ) => {
    const Icon = isBookmarked ? HiBookmark : HiOutlineBookmark

    return (
      <IconButton
        ref={ref}
        tooltip={tooltip ?? (isBookmarked ? 'Remove bookmark' : 'Add bookmark')}
        tooltipProps={{ delayDuration: 250 }}
        onClick={onClick}
        disabled={disabled || isPending || isLoading}
        className={twMerge(
          'group/bookmark-icon-button inline-flex items-center justify-center',
          className,
        )}
        {...rest}
      >
        <Icon
          size={iconSize}
          className={twMerge(
            iconClassName ?? '',
            isBookmarked || isPending
              ? twMerge(
                  'text-amber-400 dark:text-amber-400 fill-amber-400 dark:fill-amber-400',
                  activeIconClassName ?? '',
                )
              : twMerge(
                  'text-primary-500 dark:text-primary-400 group-hover/bookmark-icon-button:text-amber-500 dark:group-hover/bookmark-icon-button:text-amber-400 group-hover/bookmark-icon-button:fill-amber-400 dark:group-hover/bookmark-icon-button:fill-amber-400',
                  inactiveIconClassName ?? '',
                ),
          )}
        />
      </IconButton>
    )
  },
)

BookmarkIconButtonBase.displayName = 'BookmarkIconButtonBase'

// endregion Base

// region ToggleBookmarkIconButton

export interface ToggleBookmarkIconButtonProps extends Omit<
  IconButtonProps,
  'onClick' | 'children'
> {
  bookmarkableType: BookmarkableType
  objectId: string
  iconSize?: number
  iconClassName?: string
  inactiveIconClassName?: string
  orgId?: UUID | ''
  onBookmarkChange?: (isBookmarked: boolean) => void
}

const useBookmarkUserId = (
  orgId: UUID | '',
  bookmarkableType: BookmarkableType,
  objectId: string,
  enabled: boolean,
): UUID | null => {
  const selector = useCallback(
    (
      data: InfiniteData<PaginatedResponse<Bookmark>, PaginateRequest>,
    ): UUID | null => {
      for (const page of data.pages) {
        for (const b of page.data) {
          if (
            b.bookmarkable_type === bookmarkableType &&
            b.object_id === objectId
          ) {
            return b.user_id ?? null
          }
        }
      }
      return null
    },
    [bookmarkableType, objectId],
  )

  const { data } = useBookmarks({
    orgId,
    selector: enabled ? selector : undefined,
    prefillCheckCache: false,
  })

  return data ?? null
}

export const ToggleBookmarkIconButton = forwardRef<
  HTMLButtonElement,
  ToggleBookmarkIconButtonProps
>(
  (
    {
      bookmarkableType,
      objectId,
      iconSize = 18,
      iconClassName,
      inactiveIconClassName,
      orgId: orgIdProp,
      onBookmarkChange,
      disabled,
      tooltip: _externalTooltip,
      ...iconButtonProps
    },
    ref,
  ) => {
    const { activeOrgId } = useActiveOrgId()
    const orgId = orgIdProp ?? activeOrgId
    const orgResourceId = isUUID(orgId) ? orgId : null

    const { user: authUser } = useAuthInfo()
    const currentUserId = authUser?.userId as UUID | undefined

    const { data: checkData, isLoading: isChecking } = useCheckBookmark({
      orgId,
      bookmarkableType,
      objectId,
      enabled: !!orgId && !!objectId,
    })

    const { mutate: createBookmark, isPending: isCreating } = useCreateBookmark(
      { orgId },
    )
    const { mutate: deleteBookmark, isPending: isDeleting } = useDeleteBookmark(
      { orgId },
    )

    const isBookmarked = checkData?.is_bookmarked ?? false
    const bookmarkId = checkData?.bookmark_id
    const isPending = isCreating || isDeleting

    // Derive bookmarker info from cached list (no extra API call)
    const bookmarkerUserId = useBookmarkUserId(
      orgId,
      bookmarkableType,
      objectId,
      isBookmarked,
    )
    const isCurrentUser = !!currentUserId && bookmarkerUserId === currentUserId
    const isOtherUsersBookmark =
      isBookmarked && !!bookmarkerUserId && !isCurrentUser
    const { data: bookmarkerUser } = useOrganizationUser({
      orgId,
      userId: (isOtherUsersBookmark && bookmarkerUserId) || ('' as UUID),
    })

    const handleClick = useCallback(() => {
      if (isOtherUsersBookmark) return
      if (isBookmarked && bookmarkId) {
        deleteBookmark(
          { bookmarkId },
          {
            onSuccess: () => {
              onBookmarkChange?.(false)
              toast.success('Bookmark removed successfully.')
            },
          },
        )
      } else {
        createBookmark(
          { bookmarkable_type: bookmarkableType, object_id: objectId },
          {
            onSuccess: () => {
              onBookmarkChange?.(true)
              toast.success('Bookmark added successfully.')
            },
          },
        )
      }
    }, [
      isBookmarked,
      isOtherUsersBookmark,
      bookmarkId,
      bookmarkableType,
      objectId,
      deleteBookmark,
      createBookmark,
      onBookmarkChange,
    ])

    const tooltip = !isBookmarked
      ? 'Add bookmark'
      : isOtherUsersBookmark
        ? bookmarkerUser
          ? `Bookmarked by ${bookmarkerUser.first_name || bookmarkerUser.email}`
          : 'Bookmarked by a teammate'
        : 'Remove bookmark'

    if (IS_FEDRAMP_ENVIRONMENT || !orgResourceId) {
      return null
    }

    return (
      <ResourcePermissionGate
        resourceType="organization"
        resourceId={orgResourceId}
        permission="write"
        fallback="hide"
        asChild
      >
        <BookmarkIconButtonBase
          ref={ref}
          isBookmarked={isBookmarked}
          isPending={isPending}
          isLoading={isChecking}
          onClick={handleClick}
          iconSize={iconSize}
          iconClassName={iconClassName}
          inactiveIconClassName={inactiveIconClassName}
          tooltip={tooltip}
          disabled={disabled || isOtherUsersBookmark}
          {...iconButtonProps}
        />
      </ResourcePermissionGate>
    )
  },
)

ToggleBookmarkIconButton.displayName = 'ToggleBookmarkIconButton'

// endregion ToggleBookmarkIconButton

// region TogglePursuitBookmarkIconButton

export interface TogglePursuitBookmarkIconButtonProps extends Omit<
  IconButtonProps,
  'onClick' | 'children'
> {
  orgId: UUID | ''
  pursuitId: UUID
  iconClassName?: string
  activeIconClassName?: string
  inactiveIconClassName?: string
  iconSize?: number
  onBookmarkChange?: (isBookmarked: boolean) => void
}

export const TogglePursuitBookmarkIconButton = forwardRef<
  HTMLButtonElement,
  TogglePursuitBookmarkIconButtonProps
>(
  (
    {
      orgId,
      pursuitId,
      iconClassName,
      activeIconClassName,
      inactiveIconClassName,
      iconSize = 18,
      onBookmarkChange,
      disabled,
      tooltip: _externalTooltip,
      ...iconButtonProps
    },
    ref,
  ) => {
    const { data: isBookmarked = false, isLoading: isChecking } =
      usePursuitIsBookmarked({
        orgId,
        pursuitId,
        enabled: !!orgId && !!pursuitId,
      })

    const { mutate: createBookmark, isPending: isCreating } =
      useCreatePursuitBookmark()
    const { mutate: deleteBookmark, isPending: isDeleting } =
      useDeletePursuitBookmark()

    const isPending = isCreating || isDeleting

    const handleClick = useCallback(() => {
      if (!orgId) return
      if (isBookmarked) {
        deleteBookmark(
          { orgId, pursuitId },
          {
            onSuccess: () => {
              onBookmarkChange?.(false)
              toast.success('Bookmark removed successfully.')
            },
          },
        )
      } else {
        createBookmark(
          { orgId, pursuitId },
          {
            onSuccess: () => {
              onBookmarkChange?.(true)
              toast.success('Bookmark added successfully.')
            },
          },
        )
      }
    }, [
      isBookmarked,
      orgId,
      pursuitId,
      deleteBookmark,
      createBookmark,
      onBookmarkChange,
    ])

    return (
      <BookmarkIconButtonBase
        ref={ref}
        isBookmarked={isBookmarked}
        isPending={isPending}
        isLoading={isChecking}
        onClick={handleClick}
        iconSize={iconSize}
        iconClassName={iconClassName}
        activeIconClassName={activeIconClassName}
        inactiveIconClassName={inactiveIconClassName}
        disabled={disabled || !pursuitId || !orgId}
        {...iconButtonProps}
      />
    )
  },
)

TogglePursuitBookmarkIconButton.displayName = 'TogglePursuitBookmarkIconButton'

// endregion TogglePursuitBookmarkIconButton
